import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaImage, FaCertificate, FaGraduationCap, FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { CertificatePreviewFrame } from '../../../common/components/certificate/CertificateCanvas';
import { SAMPLE_CERTIFICATE_DATA } from '../../../common/utils/certificateLayout';

// Utility function to get the base URL for static files
const getStaticFileUrl = (filePath) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
  const serverUrl = baseUrl.replace('/api', '');
  return `${serverUrl}/${filePath}`;
};

const OrganizationCertificateTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [serverPreviewUrl, setServerPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    background_image: null,
    layout_config: {
      title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
      learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
      course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
      completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
      score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
    }
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [existingBackgroundImage, setExistingBackgroundImage] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [templateToAssign, setTemplateToAssign] = useState(null);
  const [assignSelectedCourses, setAssignSelectedCourses] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignCoursesLoading, setAssignCoursesLoading] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser?.organization_id) {
      fetchTemplates();
      fetchCourses();
    }
  }, [currentPage, searchTerm, currentUser]);

  const fetchCourses = async () => {
    if (!currentUser?.organization_id) return;
    
    try {
      setCoursesLoading(true);
      const response = await api.organizations.getCourses(currentUser.organization_id);
      let courseData = [];
      
      if (response?.data?.data && Array.isArray(response.data.data)) {
        courseData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        courseData = response.data;
      }
      
      const normalized = courseData
        .map((item) => item.course || item)
        .filter(Boolean)
        .map((course) => ({
          id: course.id,
          name: course.name || course.title,
        }));
      
      setCourses(normalized);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.auth.getProfile();
      console.log('User profile response:', response.data);
      if (response.data.success) {
        // The response structure is { data: { user: {...} } }
        const userData = response.data.data.user || response.data.data;
        setCurrentUser(userData);
        console.log('Current user:', userData);
        console.log('User organization_id:', userData.organization_id);
      } else {
        console.error('Failed to fetch user profile:', response.data.message);
        toast.error('Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to fetch user profile');
    }
  };

  const summary = useMemo(() => {
    const total = templates.length;
    const active = templates.filter(t => t.status === 1).length;
    const assignedToCourses = templates.filter(t => 
      (t.courseAssignments?.length > 0) || (t.course_count > 0)
    ).length;
    
    return { total, active, assignedToCourses };
  }, [templates]);

  const fetchTemplates = async () => {
    if (!currentUser?.organization_id) {
      console.log('No organization_id found for user:', currentUser);
      setTemplates([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        organization_id: currentUser.organization_id // Filter by current organization
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      console.log('Fetching templates with params:', params);
      const response = await api.certificates.getTemplates(params);
      console.log('Templates API response:', response.data);
      
      if (response.data.success) {
        setTemplates(response.data.data.templates || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
      } else {
        console.error('Templates API error:', response.data.message);
        toast.error('Failed to fetch certificate templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch certificate templates');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = async (mode, template = null) => {
    setModalMode(mode);
    setSelectedTemplate(template);

    if (mode === 'edit' && template) {
      let layoutConfig = template.layout_config;

      if (typeof template.layout_config === 'string') {
        try {
          layoutConfig = JSON.parse(template.layout_config);
        } catch (e) {
          console.error('Error parsing layout_config:', e);
          layoutConfig = {
            title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
            learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
            course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
            completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
            score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
          };
        }
      }

      // Preserve existing background image URL
      const bgImageUrl = template.background_image 
        ? getStaticFileUrl(template.background_image)
        : null;
      setExistingBackgroundImage(bgImageUrl);

      // Fetch assigned courses for this template
      try {
        const assignmentsResponse = await api.certificates.getAllAssignments({
          template_id: template.id,
          organization_id: currentUser?.organization_id
        });
        
        console.log('Edit modal - Assignments response:', assignmentsResponse.data);
        
        // Handle different response structures
        let assignments = [];
        if (assignmentsResponse.data.success && assignmentsResponse.data.data) {
          // Backend returns: { success: true, data: { assignments: [...], pagination: {...} } }
          if (assignmentsResponse.data.data.assignments && Array.isArray(assignmentsResponse.data.data.assignments)) {
            assignments = assignmentsResponse.data.data.assignments;
          } else if (Array.isArray(assignmentsResponse.data.data)) {
            assignments = assignmentsResponse.data.data;
          }
        } else if (Array.isArray(assignmentsResponse.data)) {
          assignments = assignmentsResponse.data;
        } else if (assignmentsResponse.data && Array.isArray(assignmentsResponse.data)) {
          assignments = assignmentsResponse.data;
        }
        
        const assignedCourseIds = assignments
          .filter(a => a && a.course_id)
          .map(a => a.course_id);
        
        console.log('Edit modal - Assigned course IDs:', assignedCourseIds);
        setSelectedCourses(assignedCourseIds);
      } catch (error) {
        console.error('Error fetching course assignments:', error);
        console.error('Error details:', error.response?.data || error.message);
        setSelectedCourses([]);
      }

      setFormData({
        name: template.name,
        description: template.description || '',
        background_image: null,
        layout_config: layoutConfig
      });
    } else {
      setExistingBackgroundImage(null);
      setSelectedCourses([]);
      setFormData({
        name: '',
        description: '',
        background_image: null,
        layout_config: {
          title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
          learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
          course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
          completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
          score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
        }
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalMode('create');
    setSelectedTemplate(null);
    setExistingBackgroundImage(null);
    setSelectedCourses([]);
    setFormData({
      name: '',
      description: '',
      background_image: null,
      layout_config: {
        title: { x: 400, y: 150, fontSize: 36, color: '#000000' },
        learner_name: { x: 400, y: 250, fontSize: 28, color: '#000000' },
        course_name: { x: 400, y: 320, fontSize: 20, color: '#000000' },
        completion_date: { x: 400, y: 380, fontSize: 16, color: '#000000' },
        score: { x: 400, y: 420, fontSize: 16, color: '#000000' }
      }
    });
    setFormErrors({});
  };

  const handleCourseToggle = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'background_image') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLayoutConfigChange = (field, property, value) => {
    setFormData(prev => ({
      ...prev,
      layout_config: {
        ...prev.layout_config,
        [field]: {
          ...prev.layout_config[field],
          [property]: property === 'color' ? value : parseInt(value) || 0
        }
      }
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('layout_config', JSON.stringify(formData.layout_config));
      
      // Automatically assign to current organization
      if (currentUser?.organization_id) {
        submitData.append('assigned_to_org', currentUser.organization_id);
      }

      if (formData.background_image) {
        submitData.append('background_image', formData.background_image);
      }

      let response;
      let templateId;
      if (modalMode === 'create') {
        response = await api.certificates.createTemplate(submitData);
        
        if (!response.data.success) {
          toast.error(response.data.message || 'Failed to create template');
          setSubmitting(false);
          return;
        }
        
        // Extract template ID from response
        templateId = response.data.data?.id || 
                     response.data.data?.template?.id || 
                     response.data.data?.template_id ||
                     response.data.template_id;
        
        if (!templateId) {
          console.error('Template ID not found in response:', response.data);
          toast.error('Template created but ID not found. Please refresh the page.');
          handleCloseModal();
          fetchTemplates();
          setSubmitting(false);
          return;
        }
      } else {
        if (!selectedTemplate?.id) {
          toast.error('Template ID is missing');
          setSubmitting(false);
          return;
        }
        
        response = await api.certificates.updateTemplate(selectedTemplate.id, submitData);
        
        if (!response.data.success) {
          toast.error(response.data.message || 'Failed to update template');
          setSubmitting(false);
          return;
        }
        
        templateId = selectedTemplate.id;
      }

        // Handle course assignments
        if (templateId && currentUser?.organization_id) {
        try {
          if (modalMode === 'edit') {
            // For edit mode: get existing assignments and sync
            const existingAssignmentsResponse = await api.certificates.getAllAssignments({
              template_id: templateId,
              organization_id: currentUser.organization_id
            });
            
            // Handle different response structures - backend returns: { success: true, data: { assignments: [...], pagination: {...} } }
            let existingAssignments = [];
            if (existingAssignmentsResponse.data.success && existingAssignmentsResponse.data.data) {
              // Backend structure: data.data.assignments
              if (existingAssignmentsResponse.data.data.assignments && Array.isArray(existingAssignmentsResponse.data.data.assignments)) {
                existingAssignments = existingAssignmentsResponse.data.data.assignments;
              } else if (Array.isArray(existingAssignmentsResponse.data.data)) {
                existingAssignments = existingAssignmentsResponse.data.data;
              }
            } else if (Array.isArray(existingAssignmentsResponse.data)) {
              existingAssignments = existingAssignmentsResponse.data;
            }
            
            const existingCourseIds = existingAssignments
              .filter(a => a && a.course_id)
              .map(a => a.course_id);
          
            // Remove unselected courses - delete records for courses that are no longer selected
            const toRemove = existingCourseIds.filter(id => !selectedCourses.includes(id));
            console.log('Edit mode - Courses to remove (unassign):', toRemove);
            
            for (const assignment of existingAssignments) {
              if (assignment && assignment.id && toRemove.includes(assignment.course_id)) {
                try {
                  console.log('Edit mode - Deleting assignment - Course ID:', assignment.course_id, 'Assignment ID:', assignment.id);
                  const unassignResponse = await api.certificates.unassignFromCourse(assignment.id);
                  if (unassignResponse.data.success) {
                    console.log('Edit mode - Successfully deleted assignment for course:', assignment.course_id);
                  } else {
                    console.warn('Edit mode - Unassign warning:', unassignResponse.data.message);
                  }
                } catch (err) {
                  console.error('Error unassigning course:', err);
                  console.error('Error details:', err.response?.data || err.message);
                }
              }
            }
          
          // Add new course assignments
          const toAdd = selectedCourses.filter(id => !existingCourseIds.includes(id));
          for (const courseId of toAdd) {
              try {
                // Validate IDs
                if (!templateId || !courseId) {
                  console.error('Missing template ID or course ID:', { templateId, courseId });
                  continue;
                }
                
                // Ensure IDs are numbers
                const templateIdNum = parseInt(templateId, 10);
                const courseIdNum = parseInt(courseId, 10);
                
                if (isNaN(templateIdNum) || isNaN(courseIdNum)) {
                  console.error('Invalid ID format:', { templateId, courseId });
                  continue;
                }
                
                const assignData = {
                  certificate_template_id: templateIdNum,
                  course_id: courseIdNum
                };
                
                // DO NOT use template_id - backend expects certificate_template_id
                // Remove any template_id if it exists
                delete assignData.template_id;
                
                if (currentUser.organization_id) {
                  assignData.organization_id = parseInt(currentUser.organization_id, 10);
                }
                
                console.log('Assigning course in edit mode - FINAL PAYLOAD:', JSON.stringify(assignData));
                
                const assignResponse = await api.certificates.assignToCourse(assignData);
                if (!assignResponse.data.success) {
                  console.warn('Assignment response not successful:', assignResponse.data);
                }
              } catch (err) {
                console.error('Error assigning course:', err);
                console.error('Error details:', err.response?.data || err.message);
              }
            }
          } else {
            // For create mode: only add new assignments
            for (const courseId of selectedCourses) {
              try {
                // Validate IDs
                if (!templateId || !courseId) {
                  console.error('Missing template ID or course ID:', { templateId, courseId });
                  continue;
                }
                
                // Ensure IDs are numbers
                const templateIdNum = parseInt(templateId, 10);
                const courseIdNum = parseInt(courseId, 10);
                
                if (isNaN(templateIdNum) || isNaN(courseIdNum)) {
                  console.error('Invalid ID format:', { templateId, courseId });
                  continue;
                }
                
                const assignData = {
                  certificate_template_id: templateIdNum,
                  course_id: courseIdNum
                };
                
                // DO NOT use template_id - backend expects certificate_template_id
                // Remove any template_id if it exists
                delete assignData.template_id;
                
                if (currentUser.organization_id) {
                  assignData.organization_id = parseInt(currentUser.organization_id, 10);
                }
                
                console.log('Assigning course in create mode - FINAL PAYLOAD:', JSON.stringify(assignData));
                
                const assignResponse = await api.certificates.assignToCourse(assignData);
                if (!assignResponse.data.success) {
                  console.warn('Assignment response not successful:', assignResponse.data);
                }
              } catch (err) {
                console.error('Error assigning course:', err);
                console.error('Error details:', err.response?.data || err.message);
              }
            }
          }
        } catch (assignmentError) {
          console.error('Error handling course assignments:', assignmentError);
          console.error('Error details:', assignmentError.response?.data || assignmentError.message);
          // Don't fail the whole operation if assignments fail
          toast.warning('Template saved but some course assignments may have failed');
          }
        }

        toast.success(`Template ${modalMode === 'create' ? 'created' : 'updated'} successfully!`);
        handleCloseModal();
        fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${modalMode} template`;
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await api.certificates.deleteTemplate(templateToDelete.id);
      if (response.data.success) {
        toast.success('Template deleted successfully!');
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        fetchTemplates();
      } else {
        toast.error(response.data.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleShowAssignModal = async (template) => {
    setTemplateToAssign(template);
    
    // Fetch current assignments for this template
    try {
      setAssignCoursesLoading(true);
      const assignmentsResponse = await api.certificates.getAllAssignments({
        template_id: template.id,
        organization_id: currentUser?.organization_id
      });
      
      console.log('Assignments response:', assignmentsResponse.data);
      
      // Handle different response structures
      let assignments = [];
      if (assignmentsResponse.data.success && assignmentsResponse.data.data) {
        assignments = Array.isArray(assignmentsResponse.data.data) 
          ? assignmentsResponse.data.data 
          : [];
      } else if (Array.isArray(assignmentsResponse.data)) {
        assignments = assignmentsResponse.data;
      } else if (Array.isArray(assignmentsResponse.data.data)) {
        assignments = assignmentsResponse.data.data;
      }
      
      const assignedCourseIds = assignments
        .filter(a => a && a.course_id)
        .map(a => a.course_id);
      
      console.log('Assigned course IDs:', assignedCourseIds);
      setAssignSelectedCourses(assignedCourseIds);
    } catch (error) {
      console.error('Error fetching course assignments:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load current course assignments');
      setAssignSelectedCourses([]);
    } finally {
      setAssignCoursesLoading(false);
    }
    
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setTemplateToAssign(null);
    setAssignSelectedCourses([]);
  };

  const handleAssignCourseToggle = (courseId) => {
    setAssignSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSaveAssignments = async () => {
    if (!templateToAssign) {
      toast.error('Template information is missing');
      return;
    }
    
    if (!templateToAssign.id) {
      toast.error('Template ID is missing');
      return;
    }
    
    if (!currentUser?.organization_id) {
      toast.error('Organization information is missing');
      return;
    }

    try {
      setAssignLoading(true);
      
      // Validate template ID is a valid number
      const templateId = parseInt(templateToAssign.id, 10);
      if (isNaN(templateId)) {
        toast.error('Invalid template ID format');
        setAssignLoading(false);
        return;
      }
      
      console.log('Saving assignments for template ID:', templateId);
      
      // Get existing assignments
      const existingAssignmentsResponse = await api.certificates.getAllAssignments({
        template_id: templateId,
        organization_id: currentUser.organization_id
      });
      
      console.log('Existing assignments response:', existingAssignmentsResponse.data);
      
      // Handle different response structures - backend returns: { success: true, data: { assignments: [...], pagination: {...} } }
      let existingAssignments = [];
      if (existingAssignmentsResponse.data.success && existingAssignmentsResponse.data.data) {
        // Backend structure: data.data.assignments
        if (existingAssignmentsResponse.data.data.assignments && Array.isArray(existingAssignmentsResponse.data.data.assignments)) {
          existingAssignments = existingAssignmentsResponse.data.data.assignments;
        } else if (Array.isArray(existingAssignmentsResponse.data.data)) {
          existingAssignments = existingAssignmentsResponse.data.data;
        }
      } else if (Array.isArray(existingAssignmentsResponse.data)) {
        existingAssignments = existingAssignmentsResponse.data;
      } else if (existingAssignmentsResponse.data && Array.isArray(existingAssignmentsResponse.data)) {
        existingAssignments = existingAssignmentsResponse.data;
      }
      
      const existingCourseIds = existingAssignments
        .filter(a => a && a.course_id)
        .map(a => a.course_id);
      
      console.log('Existing course IDs:', existingCourseIds);
      console.log('Selected course IDs:', assignSelectedCourses);
      
      // Remove unselected courses - delete records for courses that are no longer selected
      const toRemove = existingCourseIds.filter(id => !assignSelectedCourses.includes(id));
      console.log('Courses to remove (unassign):', toRemove);
      
      // Unassign (soft delete) courses that were deselected
      for (const assignment of existingAssignments) {
        if (assignment && assignment.id && toRemove.includes(assignment.course_id)) {
          try {
            console.log('Deleting assignment - Course ID:', assignment.course_id, 'Assignment ID:', assignment.id);
            const unassignResponse = await api.certificates.unassignFromCourse(assignment.id);
            console.log('Unassign response:', unassignResponse.data);
            
            if (unassignResponse.data.success) {
              console.log('Successfully deleted assignment for course:', assignment.course_id);
            } else {
              console.warn('Unassign warning:', unassignResponse.data.message);
            }
          } catch (err) {
            console.error('Error deleting assignment:', err);
            console.error('Error details:', err.response?.data || err.message);
            // Continue with other operations even if one fails
          }
        }
      }
      
      // Add new course assignments
      const toAdd = assignSelectedCourses.filter(id => !existingCourseIds.includes(id));
      console.log('Courses to add:', toAdd);
      
      const assignmentPromises = toAdd.map(async (courseId) => {
        try {
          // Validate course ID
          if (!courseId) {
            throw new Error('Course ID is missing');
          }
          
          // Ensure course ID is a number (convert if string)
          const courseIdNum = parseInt(courseId, 10);
          
          if (isNaN(courseIdNum)) {
            throw new Error('Invalid course ID format');
          }
          
          console.log('Assigning course:', courseIdNum, 'to template:', templateId);
          
          const assignData = {
            certificate_template_id: templateId,
            course_id: courseIdNum
          };
          
          // DO NOT use template_id - backend expects certificate_template_id
          // Remove any template_id if it exists
          delete assignData.template_id;
          
          // Only add organization_id if it exists
          if (currentUser.organization_id) {
            assignData.organization_id = parseInt(currentUser.organization_id, 10);
          }
          
          console.log('Assignment payload - FINAL PAYLOAD:', JSON.stringify(assignData));
          console.log('Verifying field names:', Object.keys(assignData));
          
          const assignResponse = await api.certificates.assignToCourse(assignData);
          console.log('Assign response:', assignResponse.data);
          
          if (!assignResponse.data.success) {
            const errorMsg = assignResponse.data.message || 'Failed to assign course';
            console.error('Assignment failed:', errorMsg);
            throw new Error(errorMsg);
          }
          return { success: true, courseId };
        } catch (err) {
          console.error('Error assigning course:', courseId, err);
          console.error('Error details:', err.response?.data || err.message);
          return { success: false, courseId, error: err.response?.data?.message || err.message };
        }
      });
      
      const results = await Promise.all(assignmentPromises);
      const failed = results.filter(r => !r.success);
      
      // Count successful unassignments
      const unassignCount = toRemove.length;
      const assignCount = toAdd.length;
      
      if (failed.length > 0) {
        const failedCourses = failed.map(f => f.courseId).join(', ');
        toast.warning(`Some courses failed to assign: ${failedCourses}. Please try again.`);
      } else if (assignCount > 0 || unassignCount > 0) {
        let message = 'Course assignments updated successfully!';
        if (unassignCount > 0 && assignCount > 0) {
          message = `${unassignCount} course(s) removed and ${assignCount} course(s) assigned successfully!`;
        } else if (unassignCount > 0) {
          message = `${unassignCount} course assignment(s) removed successfully!`;
        } else if (assignCount > 0) {
          message = `${assignCount} course(s) assigned successfully!`;
        }
        toast.success(message);
      } else {
        toast.info('No changes to save');
      }
      
      handleCloseAssignModal();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving course assignments:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update course assignments';
      toast.error(errorMessage);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleClosePreviewModal = () => {
    if (serverPreviewUrl) {
      URL.revokeObjectURL(serverPreviewUrl);
    }
    setServerPreviewUrl(null);
    setPreviewData(null);
    setShowPreviewModal(false);
  };

  const handleLocalPreview = async () => {
    try {
      setPreviewLoading(true);

      if (formData.background_image) {
        setPreviewData({
          backgroundImageUrl: URL.createObjectURL(formData.background_image),
          layoutConfig: formData.layout_config,
          data: SAMPLE_CERTIFICATE_DATA
        });
        setServerPreviewUrl(null);
        setShowPreviewModal(true);
        return;
      }

      const response = await api.certificates.renderImage({
        template_id: selectedTemplate?.id,
        layout_config: formData.layout_config,
        background_image: selectedTemplate?.background_image,
        sample_data: SAMPLE_CERTIFICATE_DATA
      });
      const url = URL.createObjectURL(response.data);
      setServerPreviewUrl(url);
      setPreviewData(null);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderLayoutConfigForm = () => {
    const sections = ['title', 'learner_name', 'course_name', 'completion_date', 'score'];

    return (
      <div className="mb-4">
        <h6 className="text-purple-700 font-semibold mb-3">Layout Configuration</h6>
        <p className="text-xs text-gray-500 mb-3">
          Positions use a fixed 800×600 canvas — identical to the downloaded PDF.
        </p>
        {sections.map(section => (
          <Card key={section} className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
              <small className="text-white text-capitalize font-semibold">
                {section.replace('_', ' ')} Settings
              </small>
            </Card.Header>
            <Card.Body className="bg-white">
              <Row className="g-2">
                <Col xs={6} md={3}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">X Position</label>
                    <input
                      type="number"
                      value={formData.layout_config[section]?.x || 0}
                      onChange={(e) => handleLayoutConfigChange(section, 'x', e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                    />
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Y Position</label>
                    <input
                      type="number"
                      value={formData.layout_config[section]?.y || 0}
                      onChange={(e) => handleLayoutConfigChange(section, 'y', e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                    />
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Font Size</label>
                    <input
                      type="number"
                      value={formData.layout_config[section]?.fontSize || 16}
                      onChange={(e) => handleLayoutConfigChange(section, 'fontSize', e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                    />
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        value={formData.layout_config[section]?.color || '#000000'}
                        onChange={(e) => handleLayoutConfigChange(section, 'color', e.target.value)}
                        className="w-12 h-8 rounded border border-purple-200 cursor-pointer"
                        style={{ minWidth: '48px' }}
                      />
                      <input
                        type="text"
                        value={formData.layout_config[section]?.color || '#000000'}
                        onChange={(e) => handleLayoutConfigChange(section, 'color', e.target.value)}
                        className="flex-1 px-2 py-1.5 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  const renderPreview = () => {
    if (previewLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-gray-600">Generating preview...</p>
        </div>
      );
    }

    if (serverPreviewUrl) {
      return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden p-4">
          <div className="w-full flex justify-center">
            <img
              src={serverPreviewUrl}
              alt="Certificate preview"
              style={{ width: 800, maxWidth: '100%', height: 'auto', display: 'block' }}
            />
          </div>
          <p className="text-center text-muted small mt-3 mb-0">
            Preview matches the downloaded PDF exactly (800×600 canvas).
          </p>
        </div>
      );
    }

    if (!previewData) return null;

    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden p-4">
        <CertificatePreviewFrame
          backgroundImageUrl={previewData.backgroundImageUrl}
          layoutConfig={previewData.layoutConfig}
          data={previewData.data}
        />
        <p className="text-center text-muted small mt-3 mb-0">
          Live layout preview (800×600). Save template to match PDF output exactly.
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Certificate Templates
        </h5>
      </div>

      {/* Statistics Cards */}
      <Row className="mb-4 g-3">
        <Col xs={6} sm={4} md={4}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{summary.total}</h3>
              <small className="text-white opacity-90">Total Templates</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={4} md={4}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-green-500 to-emerald-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{summary.active}</h3>
              <small className="text-white opacity-90">Active</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={4} md={4}>
          <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow rounded-xl overflow-hidden">
            <Card.Body className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white p-3">
              <h3 className="text-white mb-1 fw-bold">{summary.assignedToCourses}</h3>
              <small className="text-white opacity-90">Assigned to Courses</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} md={8} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchTerm(e.target.value);
              }}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal('create')}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Create Template</span>
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          {templates.length === 0 ? (
            <div className="text-center py-10 bg-white">
              <FaCertificate className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-gray-600 mb-2">
                {!currentUser?.organization_id 
                  ? 'No organization assigned' 
                  : 'No certificate templates found'}
              </h5>
              {currentUser?.organization_id ? (
                <>
                  <p className="text-muted text-sm mb-3">Create your first certificate template to get started.</p>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={() => handleShowModal('create')}
                    className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 px-4 py-2"
                  >
                    <FaPlus className="w-4 h-4 me-2" />
                    Create Your First Template
                  </Button>
                </>
              ) : (
                <p className="text-muted text-sm">Please contact your administrator.</p>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">Name</th>
                    <th className="py-2 px-3">Description</th>
                    <th className="py-2 px-3">Background Image</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Created By</th>
                    <th className="py-2 px-3">Assigned Courses</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <div className="fw-semibold text-gray-800 text-sm">{template.name}</div>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                        {template.description || 'No description'}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        {template.background_image ? (
                          <Badge bg="success" className="d-flex align-items-center w-fit">
                            <FaImage className="me-1" />
                            Image Set
                          </Badge>
                        ) : (
                          <Badge bg="secondary">No Image</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg={template.status === 1 ? 'success' : 'secondary'} className="text-capitalize">
                          {template.status === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                        {template.creator 
                          ? `${template.creator.first_name || ''} ${template.creator.last_name || ''}`.trim() || template.creator.email || 'Unknown'
                          : template.created_by_name || 'Unknown'}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg="info">
                          {template.courseAssignments?.length || template.course_count || 0} Courses
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex gap-1 justify-content-end">
                          <button
                            onClick={() => handleShowModal('edit', template)}
                            className="text-blue-600 hover:bg-blue-100 p-2 rounded-lg transition-all"
                            title="Edit"
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                         
                          <button
                            onClick={() => handleDelete(template)}
                            className="text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
                            title="Delete"
                          >
                            <FaTrash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered fullscreen="lg-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {modalMode === 'create' ? 'Create' : 'Edit'} Certificate Template
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Template Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                placeholder="Enter template name"
              />
              {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
            </div>
              </Col>
              <Col md={6}>
                <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Background Image</label>
              <input
                    key={`file-input-${showModal}-${selectedTemplate?.id || 'new'}`}
                type="file"
                name="background_image"
                onChange={handleInputChange}
                accept="image/*"
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF (Max: 10MB)</p>
                </div>
              </Col>
            </Row>

              {existingBackgroundImage && !formData.background_image && (
              <div className="mb-3 p-2 border border-purple-200 rounded-lg bg-white">
                  <p className="text-xs text-gray-600 mb-1">Current image:</p>
                  <img 
                    src={existingBackgroundImage} 
                    alt="Current background" 
                  className="max-w-full max-h-32 object-contain rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                placeholder="Enter template description (optional)"
              />
            </div>

            {/* Course Assignment */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Assign to Courses <span className="text-purple-600">(Optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Select courses where this certificate template will be used</p>
              {coursesLoading ? (
                <div className="text-center py-3 border border-purple-200 rounded-lg bg-white">
                  <Spinner animation="border" size="sm" />
                  <p className="text-xs text-gray-500 mt-2">Loading courses...</p>
                </div>
              ) : courses.length === 0 ? (
                <div className="border border-purple-200 rounded-lg p-3 bg-white">
                <p className="text-sm text-gray-500">No courses available for your organization.</p>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-purple-200 rounded-lg p-3 bg-white">
                  {courses.map((course) => (
                    <div key={course.id} className="mb-2 p-2 hover:bg-purple-50 rounded transition-colors">
                      <label className="d-flex align-items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCourses.includes(course.id)}
                          onChange={() => handleCourseToggle(course.id)}
                          className="form-check-input"
                        />
                        <span className="text-sm text-gray-700">{course.name}</span>
                      </label>
                    </div>
                  ))}
                  {selectedCourses.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-purple-100">
                      <p className="text-xs text-purple-600 font-semibold">
                        {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {renderLayoutConfigForm()}

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={4}>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={handleCloseModal}
                    disabled={submitting}
                    className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    Cancel
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={handleLocalPreview}
                    disabled={submitting}
                    className="w-full border-1 border-blue-600 bg-white text-blue-600 hover:from-blue-700 hover:to-cyan-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    <FaEye className="w-4 h-4" />
                    Preview
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={submitting}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        {modalMode === 'create' ? 'Create' : 'Update'} Template
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={handleClosePreviewModal} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Certificate Preview (800 × 600)</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {renderPreview()}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleClosePreviewModal}
            className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm px-4 py-2"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 text-center py-10">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Certificate Template</h5>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>"{templateToDelete?.name}"</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteCancel} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Template'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Assign Courses Modal */}
      <Modal show={showAssignModal} onHide={handleCloseAssignModal} size="lg" centered fullscreen="lg-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Assign Courses to Certificate Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {templateToAssign && (
            <>
              <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                <h6 className="text-purple-700 font-semibold mb-1">Template: {templateToAssign.name}</h6>
                <p className="text-sm text-gray-600 mb-0">
                  {templateToAssign.description || 'No description'}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Courses <span className="text-purple-600">(Optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Choose which courses should use this certificate template</p>
                {assignCoursesLoading ? (
                  <div className="text-center py-3 border border-purple-200 rounded-lg bg-white">
                    <Spinner animation="border" size="sm" />
                    <p className="text-xs text-gray-500 mt-2">Loading courses...</p>
                  </div>
                ) : coursesLoading ? (
                  <div className="text-center py-3 border border-purple-200 rounded-lg bg-white">
                    <Spinner animation="border" size="sm" />
                    <p className="text-xs text-gray-500 mt-2">Loading courses...</p>
                  </div>
                ) : courses.length === 0 ? (
                  <div className="border border-purple-200 rounded-lg p-3 bg-white">
                    <p className="text-sm text-gray-500">No courses available for your organization.</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-purple-200 rounded-lg p-3 bg-white">
                    {courses.map((course) => (
                      <div key={course.id} className="mb-2 p-2 hover:bg-purple-50 rounded transition-colors">
                        <label className="d-flex align-items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={assignSelectedCourses.includes(course.id)}
                            onChange={() => handleAssignCourseToggle(course.id)}
                            className="form-check-input"
                          />
                          <span className="text-sm text-gray-700">{course.name}</span>
                        </label>
                      </div>
                    ))}
                    {assignSelectedCourses.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-100">
                        <p className="text-xs text-purple-600 font-semibold">
                          {assignSelectedCourses.length} course{assignSelectedCourses.length !== 1 ? 's' : ''} selected
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseAssignModal}
                disabled={assignLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSaveAssignments}
                disabled={assignLoading || assignCoursesLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {assignLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaGraduationCap className="w-4 h-4" />
                    Save Assignments
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrganizationCertificateTemplatesPage;
